import { useState, useContext, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { UserPlus, Loader2, Eye, EyeOff, MapPin, ArrowLeft } from 'lucide-react';

import styles from './styles.module.css';
import { Context } from '../../../context/UserContext';
import { formatCNPJ, formatPhone, formatCEP } from '../../../utils/formatters';
import SubscriptionCheckoutModal from '../../../componentes/SubscriptionModal';
import api from '../../../utils/api';
import useFlashMessage from '../../../hooks/useFlashMessage';

// Mapa plano → nome exibido, nome no banco e valor (usado no Brick)
const PLAN_CONFIG = {
    free:    { label: 'Gratuito',                planName: null,                       amount: 0 },
    plus:    { label: 'Plus — R$ 12,90/mês',      planName: 'Plano Plus - Mensal',      amount: 12.90 },
    premium: { label: 'Premium — R$ 21,90/mês',   planName: 'Plano Premium - Mensal',   amount: 21.90 },
};

function Register() {
    const { register, user } = useContext(Context);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setFlashMessage } = useFlashMessage();

    const [form, setForm] = useState({
        name: '', email: '', phone: '', cnpj: '',
        password: '', confirmpassword: '',
        plan: 'free',
        localization: { address: '', city: '', state: '', cep: '' },
    });

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm,  setShowConfirm]  = useState(false);
    const [loading, setLoading]           = useState(false);
    const [cepLoading, setCepLoading]     = useState(false);
    const [cepError, setCepError]         = useState('');
    const [showCheckout, setShowCheckout] = useState(false);

    // Lê ?plan= da URL
    useEffect(() => {
        const planFromUrl = searchParams.get('plan');
        if (planFromUrl && PLAN_CONFIG[planFromUrl]) {
            setForm(prev => ({ ...prev, plan: planFromUrl }));
        }
    }, [searchParams]);

    /* ── ViaCEP ── */
    async function fetchCep(digits) {
        setCepLoading(true);
        setCepError('');
        try {
            const res  = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
            const data = await res.json();
            if (data.erro) { setCepError('CEP não encontrado.'); return; }
            setForm(prev => ({
                ...prev,
                localization: {
                    ...prev.localization,
                    cep:     formatCEP(digits),
                    address: data.logradouro || prev.localization.address,
                    city:    data.localidade  || prev.localization.city,
                    state:   data.uf          || prev.localization.state,
                },
            }));
        } catch {
            setCepError('Erro ao buscar CEP. Preencha manualmente.');
        } finally {
            setCepLoading(false);
        }
    }

    /* ── handleChange ── */
    function handleChange(e) {
        const { name, value } = e.target;

        if (['address', 'city', 'state', 'cep'].includes(name)) {
            const formatted = name === 'cep' ? formatCEP(value) : value;
            setForm(prev => ({
                ...prev,
                localization: { ...prev.localization, [name]: formatted },
            }));
            if (name === 'cep') {
                const digits = value.replace(/\D/g, '');
                if (digits.length === 8) fetchCep(digits);
                else setCepError('');
            }
            return;
        }

        let formatted = value;
        if (name === 'phone') formatted = formatPhone(value);
        if (name === 'cnpj')  formatted = formatCNPJ(value);

        setForm(prev => ({ ...prev, [name]: formatted }));
    }

    /* ── Submit ──
       Plano Gratuito → cria a conta na hora, como sempre.
       Plano pago      → NÃO cria conta nenhuma ainda. Só abre o modal
                          do cartão. A conta só é criada depois que o
                          cartão é tokenizado com sucesso (dentro do
                          próprio SubscriptionCheckoutModal, via
                          preRegisterData).
    ── */
    async function handleSubmit(e) {
        e.preventDefault();

        // ── Validações client-side ──
        // Importante: no fluxo de plano pago, register() só roda depois
        // do pagamento ser aprovado. Se deixássemos essas checagens só
        // pro backend (dentro do register), a pessoa poderia ter o
        // cartão cobrado e só DEPOIS descobrir que algum dado estava
        // errado. Por isso validamos tudo aqui ANTES de abrir o modal.

        if (!form.name.trim()) {
            setFlashMessage('Informe seu nome completo.', 'error');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(form.email)) {
            setFlashMessage('Informe um e-mail válido.', 'error');
            return;
        }

        if (form.cnpj && form.cnpj.replace(/\D/g, '').length !== 14) {
            setFlashMessage('CNPJ deve conter 14 dígitos (ou deixe em branco).', 'error');
            return;
        }

        if (form.password !== form.confirmpassword) {
            setFlashMessage('As senhas não coincidem.', 'error');
            return;
        }
        if (form.password.length < 8) {
            setFlashMessage('A senha precisa ter no mínimo 6 caracteres.', 'error');
            return;
        }

        // ── Validação server-side (e-mail em uso, CNPJ) ──
        // Reaproveita a MESMA rota /users/register, só que com
        // validateOnly:true — roda as mesmas checagens do backend
        // sem criar a conta de fato.
        setLoading(true);
        try {
            await api.post('/users/register?validateOnly=true', form);
        } catch (err) {
            setFlashMessage(err.response?.data?.message || 'Não foi possível validar os dados. Tente novamente.', 'error');
            setLoading(false);
            return;
        }

        const selectedPlan = form.plan;
        const planConfig   = PLAN_CONFIG[selectedPlan];

        if (selectedPlan !== 'free' && planConfig?.planName) {
            // Todas as validações passaram — agora sim pode abrir o
            // checkout. register() só roda depois do pagamento, dentro
            // do próprio modal.
            setLoading(false);
            setShowCheckout(true);
            return;
        }

        // Plano free: fluxo normal, cria a conta direto.
        await register(form);
        setLoading(false);
        navigate('/dashboard');
    }

    const selectedPlanLabel = PLAN_CONFIG[form.plan]?.label || 'Gratuito';
    const selectedPlanConfig = PLAN_CONFIG[form.plan];

    return (
        <div className={styles.page}>
            <div className={styles.card}>

                <Link to="/" className={styles.back}>
                    <ArrowLeft size={15} /> Voltar ao início
                </Link>

                <div className={styles.header}>
                    <div className={styles.iconWrap}><UserPlus size={24} /></div>
                    <h1 className={styles.title}>Criar conta</h1>
                    <p className={styles.sub}>Comece a emitir notas de serviço hoje mesmo.</p>
                </div>

                {form.plan !== 'free' && (
                    <div className={styles.planBanner}>
                        🎉 Plano selecionado: <strong>{selectedPlanLabel}</strong>.
                        Sua conta só será criada depois de confirmar o pagamento na próxima etapa.
                    </div>
                )}

                <form onSubmit={handleSubmit} className={styles.form}>

                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label>Nome completo *</label>
                            <input name="name" type="text" placeholder="Seu nome"
                                value={form.name} onChange={handleChange} required />
                        </div>
                        <div className={styles.field}>
                            <label>E-mail *</label>
                            <input name="email" type="email" placeholder="seu@email.com"
                                value={form.email} onChange={handleChange} required />
                        </div>
                    </div>

                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label>Telefone</label>
                            <input name="phone" type="text" placeholder="(11) 99999-9999"
                                value={form.phone} onChange={handleChange} />
                        </div>
                        <div className={styles.field}>
                            <label>CNPJ</label>
                            <input name="cnpj" type="text" placeholder="00.000.000/0000-00"
                                value={form.cnpj} onChange={handleChange} />
                        </div>
                    </div>

                    <div className={styles.locationBox}>
                        <div className={styles.locationHeader}>
                            <MapPin size={14} className={styles.locationIcon} />
                            <span className={styles.locationTitle}>Localização (opcional)</span>
                        </div>

                        <div className={styles.field}>
                            <label>CEP</label>
                            <div className={styles.cepInputWrap}>
                                <input name="cep" type="text" placeholder="00000-000"
                                    value={form.localization.cep} onChange={handleChange} />
                                {cepLoading && <Loader2 size={15} className={styles.cepSpinner} />}
                            </div>
                            {cepError && <p className={styles.cepError}>{cepError}</p>}
                        </div>

                        <div className={styles.field}>
                            <label>Endereço</label>
                            <input name="address" type="text" placeholder="Rua, número, bairro"
                                value={form.localization.address} onChange={handleChange} />
                        </div>

                        <div className={styles.row}>
                            <div className={styles.field}>
                                <label>Cidade</label>
                                <input name="city" type="text" placeholder="São Paulo"
                                    value={form.localization.city} onChange={handleChange} />
                            </div>
                            <div className={styles.fieldSmall}>
                                <label>UF</label>
                                <input name="state" type="text" placeholder="SP" maxLength={2}
                                    value={form.localization.state} onChange={handleChange} />
                            </div>
                        </div>
                    </div>

                    <div className={styles.field}>
                        <label>Plano</label>
                        <select name="plan" value={form.plan}
                            onChange={e => setForm(prev => ({ ...prev, plan: e.target.value }))}
                            className={styles.select}>
                            <option value="free">Gratuito — R$ 0/mês</option>
                            <option value="plus">Plus — R$ 12,90/mês (cobrança recorrente)</option>
                            <option value="premium">Premium — R$ 21,90/mês (cobrança recorrente)</option>
                        </select>
                    </div>

                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label>Senha *</label>
                            <div className={styles.passWrap}>
                                <input name="password" type={showPassword ? 'text' : 'password'}
                                    placeholder="Mínimo 8 caracteres"
                                    value={form.password} onChange={handleChange} required />
                                <button type="button" className={styles.eyeBtn}
                                    onClick={() => setShowPassword(v => !v)}>
                                    {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                                </button>
                            </div>
                        </div>
                        <div className={styles.field}>
                            <label>Confirmar senha *</label>
                            <div className={styles.passWrap}>
                                <input name="confirmpassword" type={showConfirm ? 'text' : 'password'}
                                    placeholder="Repita a senha"
                                    value={form.confirmpassword} onChange={handleChange} required />
                                <button type="button" className={styles.eyeBtn}
                                    onClick={() => setShowConfirm(v => !v)}>
                                    {showConfirm ? <Eye size={16} /> : <EyeOff size={16} />}
                                </button>
                            </div>
                        </div>
                    </div>

                    <button type="submit" className={styles.btn} disabled={loading}>
                        {loading
                            ? <Loader2 size={18} className={styles.spinner} />
                            : form.plan === 'free'
                                ? 'Criar conta grátis'
                                : `Continuar e assinar ${PLAN_CONFIG[form.plan]?.label.split('—')[0].trim()}`
                        }
                    </button>
                </form>

                <p className={styles.loginLink}>
                    Já tem conta? <Link to="/login">Entrar</Link>
                </p>
            </div>

            {showCheckout && selectedPlanConfig?.planName && (
                <SubscriptionCheckoutModal
                    plan={{
                        planName: selectedPlanConfig.planName,
                        name:     selectedPlanLabel.split('—')[0].trim(),
                        amount:   selectedPlanConfig.amount,
                    }}
                    preRegisterData={form}
                    onClose={() => setShowCheckout(false)}
                    onSuccess={() => {
                        setShowCheckout(false);
                        window.location.href = '/dashboard';
                    }}
                />
            )}
        </div>
    );
}

export default Register;
